import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdminLayout from "@/components/AdminLayout";
import Landing from "@/pages/Landing";
import { Login, Signup } from "@/pages/Auth";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import AccountSecurity from "@/pages/AccountSecurity";
import Book from "@/pages/Book";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import ClientDashboard from "@/pages/ClientDashboard";
import MyBookings from "@/pages/MyBookings";
import CleanerOnboarding from "@/pages/CleanerOnboarding";
import CleanerPortal from "@/pages/CleanerPortal";
import CleanerRiskCenter from "@/pages/CleanerRiskCenter";
import CleanerAvailableJobs from "@/pages/CleanerAvailableJobs";
import CleanerSchedule from "@/pages/CleanerSchedule";
import CleanerEarnings from "@/pages/CleanerEarnings";
import CleanerSafetyChecklist from "@/pages/CleanerSafetyChecklist";
import ClientProfile from "@/pages/ClientProfile";
import SupportCenter from "@/pages/SupportCenter";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminReports from "@/pages/AdminReports";
import AdminSettings from "@/pages/AdminSettings";
import AdminSupportInbox from "@/pages/AdminSupportInbox";
import AdminRiskCenter from "@/pages/AdminRiskCenter";
import { ServiceAgreement, Privacy, Cancellation, CleanerAgreement, DisputePolicy } from "@/pages/Legal";
import Services from "@/pages/Services";

function Protected({ children, roles }) {
  const { user, loading } = useApp();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// Public site chrome — Navbar + Footer. Not used for /admin/*.
function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Admin — persistent shell, no public nav/footer/CTA. Refresh keeps the
          exact sub-route because these are real URLs and the JWT lives in
          localStorage (see AppContext). */}
      <Route
        path="/admin"
        element={<Protected roles={["admin"]}><AdminLayout /></Protected>}
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="risk" element={<AdminRiskCenter />} />
        <Route path="audit" element={<AdminAuditLog />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="messages" element={<AdminSupportInbox />} />
        <Route path=":section" element={<AdminDashboard />} />
      </Route>

      {/* Public site + role-scoped pages — get the public Navbar/Footer chrome */}
      <Route
        path="*"
        element={
          <PublicLayout>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/services" element={<Services />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account/security" element={<Protected><AccountSecurity /></Protected>} />
              <Route path="/book" element={<Book />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/dashboard/profile" element={<Protected roles={["customer"]}><ClientProfile /></Protected>} />
              <Route path="/dashboard/bookings" element={<Protected roles={["customer"]}><MyBookings /></Protected>} />
              <Route path="/dashboard" element={<Protected roles={["customer"]}><ClientDashboard /></Protected>} />
              <Route path="/support" element={<Protected><SupportCenter /></Protected>} />
              <Route path="/cleaner/onboarding" element={<Protected roles={["cleaner"]}><CleanerOnboarding /></Protected>} />
              <Route path="/cleaner/onboarding-checklist" element={<Protected roles={["cleaner"]}><CleanerRiskCenter /></Protected>} />
              <Route path="/cleaner/available" element={<Protected roles={["cleaner"]}><CleanerAvailableJobs /></Protected>} />
              <Route path="/cleaner/schedule" element={<Protected roles={["cleaner"]}><CleanerSchedule /></Protected>} />
              <Route path="/cleaner/earnings" element={<Protected roles={["cleaner"]}><CleanerEarnings /></Protected>} />
              <Route path="/cleaner/safety-checklist" element={<Protected roles={["cleaner"]}><CleanerSafetyChecklist /></Protected>} />
              <Route path="/cleaner" element={<Protected roles={["cleaner"]}><CleanerPortal /></Protected>} />
              <Route path="/legal/service-agreement" element={<ServiceAgreement />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/cancellation" element={<Cancellation />} />
              <Route path="/legal/cleaner-agreement" element={<CleanerAgreement />} />
              <Route path="/legal/dispute-policy" element={<DisputePolicy />} />
            </Routes>
          </PublicLayout>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
